import * as React from 'react';
import { Autocomplete, Box, Button, CircularProgress, DialogActions, DialogContent, TextField } from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { GlobalContext } from '../../Context/GlobalContext';
import { AxiosService } from '../../Services/AxiosService';

import { useAuth } from "react-oidc-context";
import { useNavigate } from 'react-router-dom';
import DoneIcon from '@mui/icons-material/Done';
import CloseIcon from '@mui/icons-material/Close';
import useAxios from '../../Hooks/useAxios';
import { ALTA_TASK, BAJA_TASK, DATE_FORMAT } from '../../Constants/Global';

/**
 * A form component for adding a new task, supporting two types of tasks: `ALTA_TASK` (add employee) and `BAJA_TASK` (remove employee).
 * The form dynamically adjusts its fields based on the task type and includes validation for user inputs.
 * 
 * @component Components/AddTaskForm
 * @param {Object} props - The component props.
 * @param {string} props.taskType - The type of task to add (`ALTA_TASK` or `BAJA_TASK`).
 * @returns {JSX.Element} The rendered form component.
 * @example
 * <AddTaskForm taskType={ALTA_TASK} />
 */
function AddTaskForm(props) {  
  const { taskType } = props;
  const navigate = useNavigate();
  const { setNewTaskOpen, eventDate, setAlertBarOpen, setAlertBarMessage, setAlertBarSeverity } = React.useContext(GlobalContext);
  const [name, setName] = React.useState("");
  const [nameError, setNameError] = React.useState(false);
  const [department, setDepartment] = React.useState({});
  const [center, setCenter] = React.useState({});
  //CC NEW 2026-10-20 11:30
  const [centerId, setCenterId] = React.useState(null);
  const [surname, setSurname] = React.useState("");
  const [surnameError, setSurnameError] = React.useState(false);
  const [tool, setTool] = React.useState("");
  const [toolError, setToolError] = React.useState(false);
  const [hafCode, setHafCode] = React.useState("");
  const [hafCodeError, setHafCodeError] = React.useState(false);
  //const [center, setCenter] = React.useState("");
  const [centerError, setCenterError] = React.useState(false);
  const [positionGeneric, setPositionGeneric] = React.useState("");
  const [positionGenericError, setPositionGenericError] = React.useState(false);
  const [email, setEmail] = React.useState("");
  const [emailError, setEmailError] = React.useState(false);
  const [phone, setPhone] = React.useState("");
  const [phoneError, setPhoneError] = React.useState(false);
  const [endDate, setEndDate] = React.useState("");
  const { data, isLoading, error } = useAxios("departments_employees");
  const [departments, setDepartments] = React.useState([]);
  const [loadingPOST, setLoadingPOST] = React.useState(false);
  const [employees, setEmployees] = React.useState([]);
  const [employeeId, setEmployeeId] = React.useState([]);
  const [centers, setCenters] = React.useState([]);

  const auth = useAuth();


  /**
   * Fetches department and employee data on component mount and updates the state.
   * Displays an error message if the request fails.
   */
  React.useEffect(() => {
    if (!isLoading) {
      if (error) {
        setAlertBarSeverity("error");
        setAlertBarMessage(error);
        setAlertBarOpen(true);
      } else {
        setDepartments([...data.departments.map(item => { return {value: item.id, label: item.name}})]);
        setEmployees([...data.employees.map(item => { return {value: item.id, label: `${item.fullName} (${item.email})`}})]);
        setCenters([...data.centers.map(item => { return {value: item.id, label: item.name}})]);
      }
    }
  }, [isLoading]);

  /**
   * Handles form submission. Validates the form and sends a POST request to add a new task.
   * Displays success or error messages based on the API response.
   * 
   * @param {Event} event - The form submission event.
   */
  const handleSubmit = (event) => {
    event.preventDefault();
    if (event.target.checkValidity()) {
      setLoadingPOST(true);
      switch (taskType){
        case ALTA_TASK:
          const requestBody =  {
            process: {
                typeId: taskType,
                endDate: endDate !== ""? endDate : eventDate
            },
            targetEmployee: {
                name: name,
                surname: surname,
                departmentId: department.id,
                email: email,
                phone: phone,
                tool: tool,
                hafCode: hafCode,
                positionGeneric: positionGeneric,
                //CC MOD 2026-10-20 11:30
                //center: center
                center: center,
                //CC NEW 2026-10-20 11:30
                centerId: centerId
                // birthDate: birthDate !== ""? birthDate: null,
                // hireDate: eventDate
            }
          }

            console.log("requestBody: ", requestBody);

          AxiosService.post_new_task(auth.user?.access_token, requestBody)
          .then((response) => {

            console.log("response: ", response);

            setLoadingPOST(false);
            setAlertBarSeverity("success");
            setAlertBarMessage(response.data.message);
            setAlertBarOpen(true);
            setNewTaskOpen(false);
            navigate("/#");
          })
          .catch((err) => {
            setAlertBarSeverity("error");
            setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
            setAlertBarOpen(true);
          })
          .finally(() => {            
            setLoadingPOST(false);
          });
        break;

        case BAJA_TASK: {
          const requestBody =  {
            process: {
                typeId: taskType,
                endDate: endDate !== ""? endDate : eventDate
            },
            targetEmployee: {
                id: employeeId,
                surname: surname,
                departmentId: department.id,
                email: email,
                phone: phone,
                tool: tool
                // birthDate: birthDate !== ""? birthDate: null,
                // hireDate: eventDate
            }
          }


          AxiosService.post_new_task(auth.user?.access_token, requestBody)
          .then((response) => {
            setLoadingPOST(false);
            setAlertBarSeverity("success");
            setAlertBarMessage(response.data.message);
            setAlertBarOpen(true);
            setNewTaskOpen(false);
            navigate("/#");
          })
          .catch((err) => {
            setAlertBarSeverity("error");
            setAlertBarMessage(`${err.message} (${err.response.statusText}): ${err.response.data.error}`);
            setAlertBarOpen(true);
          })
          .finally(() => {            
            setLoadingPOST(false);
          });
        }
        break;

        default:
          break;
      }
    } else {
      setAlertBarSeverity("warning");
      setAlertBarMessage("Form is invalid! Please check the fields.");
      setAlertBarOpen(true);
    }
  };

  /**
   * Handles changes to the email input field and updates the state.
   * 
   * @param {Event} event - The input change event.
   */
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    if (event.target.validity.valid) {
      setEmailError(false);
    } else {
      setEmailError(true);
    }
  }

  /**
   * Handles changes to the name input field and updates the state.
   * 
   * @param {Event} event - The input change event.
   */
  const handleNameChange = (event) => {
    setName(event.target.value);
    if (event.target.validity.valid) {
      setNameError(false);
    } else {
      setNameError(true);
    }
  }


  const handleCenterChange = (event, values) => {
      setCenter(values && values.label);
      //CC NEW 2026-10-20 11:30
      //$$ las opciones son {value: item.id, label: item.name}
      setCenterId(values && values.value);
    }

  /**
   * Handles changes to the department dropdown and updates the state.
   * 
   * @param {Event} event - The change event.
   * @param {Object} values - The selected department values.
   */
  const handleDepartmentChange = (event, values) => {
    setDepartment(values && {name: values.label, id: parseInt(values.value)});
  }

  /**
   * Handles changes to the surname input field and updates the state.
   * 
   * @param {Event} event - The input change event.
   */
  const handleSurnameChange = (event) => {
    setSurname(event.target.value);
    if (event.target.validity.valid) {
      setSurnameError(false);
    } else {
      setSurnameError(true);
    }
  }

    /**
     * Handles changes to the surname input field and updates the state.
     *
     * @param {Event} event - The input change event.
     */
    const handleToolChange = (event) => {
    console.log("event.target.value1: ", event.target.value)
      setTool(event.target.value);
      if (event.target.validity.valid) {
        setToolError(false);
        console.log("event.target.value2: ", event.target.value)
      } else {
        console.log("event.target.value3: ", event.target.value)
        setToolError(true);
      }
    }

   /**
       * Handles changes to the surname input field and updates the state.
       *
       * @param {Event} event - The input change event.
       */
      const handleHafCodeChange = (event) => {
      console.log("event.target.value1: ", event.target.value)
        setHafCode(event.target.value);
        if (event.target.validity.valid) {
          setHafCodeError(false);
        } else {
          setHafCodeError(true);
        }
      }


      /**
       * Handles changes to the surname input field and updates the state.
       *
       * @param {Event} event - The input change event.
       */
      const handlePositionGenericChange = (event) => {
      console.log("event.target.value1: ", event.target.value)
        setPositionGeneric(event.target.value);
        if (event.target.validity.valid) {
          setPositionGenericError(false);
        } else {
          setPositionGenericError(true);
        }
      }



  /**
   * Handles changes to the phone input field and updates the state.
   * 
   * @param {Event} event - The input change event.
   */
  const handlePhoneChange = (event) => {
    setPhone(event.target.value);
    if (event.target.validity.valid) {
      setPhoneError(false);
    } else {
      setPhoneError(true);
    }
  }

  /**
  * Handles changes to the phone input field and updates the state.
  *
  * @param {Event} event - The input change event.
  */
  // const handleCenterChange = (event) => {
  //  setCenter(event.target.value);
  //    if (event.target.validity.valid) {
  //      setCenterError(false);
  //    } else {
  //      setCenterError(true);
  //    }
  //  }


  // const handleBirthDateChange = (event) => {
  //   setBirthDate(event.format(dateFormat));
  // }

  /**
   * Handles changes to the end date picker and updates the state.
   * 
   * @param {Object} event - The date picker change event.
   */
  const handleEndDateChange = (event) => {
    setEndDate(event.format(DATE_FORMAT));
  }

  /**
   * Closes the form when the user clicks outside or presses the Escape key.
   * 
   * @param {Event} event - The event triggering the close action.
   */
  const handleClose = (event) => {    
    if (event.nativeEvent.code === "Escape"
      || (event.target.tagName !== "H2"
        && event.target.tagName !== "LI"
        && event.target.tagName !== "DIV")){
          setNewTaskOpen(false);
    }
  };

  /**
   * Handles changes to the employee dropdown and updates the state.
   * 
   * @param {Event} event - The change event.
   * @param {Object} values - The selected employee values.
   */
  const handleEmployeeChange = (event, values) => {
    setEmployeeId(values && parseInt(values.value));
  }

  return (
    <>      
      <Box component="form"
      onSubmit={handleSubmit}
       noValidate
       sx={{
       display: 'flex',
       flexDirection: 'column',
       }}
       >

        <DialogContent
        sx={{
        minHeight: '10vh',
        maxHeight: '30vh',
        overflowY: 'auto',

        }}>
          {
            taskType === ALTA_TASK? 
            <>    
              <TextField
                id="nameInput"
                error={nameError}
                value={name}
                label="Name"
                required
                onChange={handleNameChange}
                helperText={nameError && "Invalid format"}
                inputProps={{
                  pattern: "[A-Za-z ]+"
                }}
                sx={{ width: 360 , marginTop: "10px"}}
              />
              <br></br>
              <TextField
                id="surnameInput"
                error={surnameError}
                value={surname}
                label="Surname"
                required
                onChange={handleSurnameChange}
                helperText={nameError && "Invalid format"}
                inputProps={{
                  pattern: "[A-Za-z ]+"
                }}
                sx={{ width: 360 , marginTop: "10px"}}
              />
              <Autocomplete
                disablePortal
                required
                options={departments}
                onChange={handleDepartmentChange}
                sx={{ width: 360 , marginTop: "10px"}}
                renderInput={(params) => <TextField {...params} label="Department"/>}
                ListboxProps={{ style: { maxHeight: '15rem' } }}
                noOptionsText={isLoading? <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</> : error && error || "No options"}
              /> 
              <TextField
                id="emailInput"
                error={emailError}
                value={email}
                label="Email"
                required
                onChange={handleEmailChange}
                helperText={emailError && "Invalid format"}
                inputProps={{ type: "email" }}
                sx={{ width: 360 , marginTop: "10px"}}
              />
              <br></br>
              <TextField
                id="phoneInput"
                error={phoneError}
                value={phone}
                label="Phone"
                onChange={handlePhoneChange}
                helperText={phoneError && "Invalid format"}
                inputProps={{ type: "tel" , pattern:"\\+?[0-9]{9,12}"}}
                sx={{ width: 360 , marginTop: "10px"}}
              />

              <br></br>
               <TextField
                 id="toolInput"
                 error={toolError}
                 value={tool}
                 label="Organization"
                 onChange={handleToolChange}
                 /*helperText={toolError && "Invalid format"}
                 inputProps={{ type: "tel" , pattern:"\\+?[0-9]{9,12}"}}*/
                 sx={{ width: 360 , marginTop: "10px"}}
               />

               <br></br>
               <TextField
                 id="hafInput"
                 error={hafCodeError}
                 value={hafCode}
                 label="HAF code"
                 onChange={handleHafCodeChange}
                 helperText={hafCodeError && "Invalid format"}
                 inputProps={{ type: "text" , pattern:"[A-Za-z0-9]+"}}
                 sx={{ width: 360 , marginTop: "10px"}}
               />

               <br></br>
               <TextField
                 id="positionGenericInput"
                 error={positionGenericError}
                 value={positionGeneric}
                 label="Position (Generic)"
                 onChange={handlePositionGenericChange}
                 /*helperText={positionGenericError && "Invalid format"}
                 inputProps={{ type: "text" , pattern:"[A-Za-z0-9áéíóúÁÉÍÓÚñÑ]+"}}*/
                 sx={{ width: 360 , marginTop: "10px"}}
               />

                <Autocomplete
                  disablePortal
                  required
                  options={centers}
                  onChange={handleCenterChange}
                  sx={{ width: 360 , marginTop: "10px"}}
                  renderInput={(params) => <TextField {...params} label="Centers"/>}
                  ListboxProps={{ style: { maxHeight: '15rem' } }}
                  noOptionsText={isLoading? <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</> : error && error || "No options"}
                />

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                {/* <Box pt={"10px"}>
                  <DatePicker sx={{ width: 360 , marginTop: "10px"}} format={DATE_FORMAT} label="Birth date" onChange={handleBirthDateChange}/>
                </Box> */}
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box pt={"10px"}>
                  <DatePicker sx={{ width: 360 , marginTop: "10px"}} format={DATE_FORMAT} label="End date" defaultValue={dayjs(eventDate, DATE_FORMAT)} onChange={handleEndDateChange}/>
                </Box>
              </LocalizationProvider>
            </> :
            taskType === BAJA_TASK? 
            <>
              <Autocomplete
                disablePortal
                required
                options={employees}
                onChange={handleEmployeeChange}
                sx={{ width: 360 , marginTop: "10px"}}
                renderInput={(params) => <TextField {...params} label="Employee" />}
                ListboxProps={{ style: { maxHeight: '6rem' } }}
                noOptionsText={isLoading? <><CircularProgress size="1rem" sx={{marginRight: "4px"}} />Loading...</> : error && error || "No options"}
              /> 
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box pt={"10px"}>
                  <DatePicker sx={{ width: 360 , marginTop: "10px"}} format={DATE_FORMAT} label="End date" defaultValue={dayjs(eventDate, DATE_FORMAT)} onChange={handleEndDateChange}/>
                </Box>
              </LocalizationProvider>
            </> : <></>
          }
        </DialogContent>
        <DialogActions
        sx={{
           flexShrink: 0
          }}
         >

        <Button color="error" sx={{ marginRight: "auto", fontWeight:'fontWeightBold' }} onClick={handleClose}>
          <CloseIcon/> Cancel
        </Button>
        <Button loading={loadingPOST} color="success" sx={{ fontWeight:'fontWeightBold' }} type="submit">
          <DoneIcon /> Accept
        </Button>
      </DialogActions>
      </Box>
  </>
  );
}

export default AddTaskForm;
